import { classNames } from '@/util/tailwindHelpers'
import Icon from '@/components/Icon'
import { USDC } from '@/components/USDC'
import useBreakpoints from '@/hooks/useBreakpoints'
import { numberWithCommas, roundToTwoDecimals } from '@/util/numberHelpers'
import { isNull } from '@/util/typeHelper'
import { AssetsValues } from '@/api/resources/user/assets/get/schema'
import Tooltip from '@/components/Tooltip'
import { useModal } from '@ebay/nice-modal-react'
import { useState } from 'react'
import { useLayoutData } from '@/contexts/layout'
import { AssetSelectionMode } from '../types'
import BooleanModal from '@/components/Modals/ModalV2/BooleanModal'

interface Props extends AssetsValues {
  className?: string
}

const ArcadeValuesSection = ({
  className,
  points_available,
  inventory_value
}: Props) => {
  const { isMobile } = useBreakpoints()

  return isMobile ? (
    <div
      className={classNames(
        'rounded-[16px] border-gray-800 border-1',
        className
      )}
    >
      <div className="py-[11px] px-[14px] flex gap-2">
        <div className="flex-1 inline-flex overflow-hidden">
          <EstimatedInventoryValue value={inventory_value} toolTip />
        </div>
        <div className="border-1 border-solid border-gray-800 w-[1px] h-[10vh] max-h-[44px]" />
        <div className="flex-1 overflow-hidden">
          <PointsValue value={points_available} />
        </div>
      </div>
    </div>
  ) : (
    <div
      className={classNames('inline-flex flex-col gap-1 body-sm', className)}
    >
      <EstimatedInventoryValue value={inventory_value} toolTip />
      <PointsValue value={points_available} />
    </div>
  )
}

export default ArcadeValuesSection

const PointsValue = ({ value }: { value: number | null }) => {
  return (
    <ArcadeValueContent
      label="Available Points"
      value={{
        type: 'points',
        value
      }}
    />
  )
}

const EstimatedInventoryValue = ({
  value,
  toolTip
}: {
  value: number | null
  toolTip?: boolean
}) => {
  return (
    <ArcadeValueContent
      label="Estimated Value"
      value={{
        type: 'usdc',
        value
      }}
      toolTip={toolTip}
    />
  )
}

const USDCValueText = ({
  selectionMode,
  value
}: {
  selectionMode: AssetSelectionMode
  value: number
}) => {
  const className = selectionMode === 'convert' ? 'text-white' : 'text-success'

  return <USDC suffixClassName="body-xs" className={className} price={value} />
}

const PointsValueText = ({
  selectionMode,
  value
}: {
  selectionMode: AssetSelectionMode
  value: number
}) => {
  const baseClass = 'body-sm'
  const colorClass =
    selectionMode === 'convert' ? 'text-arcadeGreen' : 'text-white'
  const nullClass = isNull(value) ? 'text-gray-300' : ''

  const displayValue = isNull(value)
    ? 'TBA'
    : numberWithCommas(roundToTwoDecimals(value))

  return (
    <span className={classNames(baseClass, colorClass, nullClass)}>
      {displayValue}
    </span>
  )
}

const ArcadeValueContent = ({
  label,
  value,
  toolTip
}: {
  label: string
  value: {
    type: 'usdc' | 'points'
    value: number | null
  }
  toolTip?: boolean
  valueClasses?: string
  extraContent?: React.ReactNode
}) => {
  const [mobileToolTipOpen, setMobileToolTipOpen] = useState(false)
  const { isMobile } = useBreakpoints()

  const { selectionMode } = useLayoutData()

  const modal = useModal(BooleanModal)

  const topLabelClasses = `text-gray-300 mb-half ${
    isMobile ? 'whitespace-nowrap overflow-ellipsis overflow-hidden' : ''
  }`

  const isUsdc = value.type === 'usdc'

  const handleToolTipClick = () => {
    if (!isMobile) return // Don't show modal if not on mobile

    setMobileToolTipOpen(!mobileToolTipOpen)
    modal.show({
      children: (
        <p className="text-gray-300 body-md">
          Estimated value is based on Droppp Marketplace data and is for
          illustrative purposes only. There is no assurance that the value of
          your collection will match the figures presented. This estimate can be
          delayed up to 15 minutes.
        </p>
      ),
      title: 'Estimated Value',
      primaryButton: {
        label: 'Ok',
        onClick: modal.hide
      }
    })
  }
  const renderToolTip = () => {
    if (isMobile) {
      return (
        <div className="flex gap-1 text-gray-300">
          <div className={topLabelClasses}>{label}</div>
          <div onClick={handleToolTipClick}>
            <Icon name="question" className="w-full" />
          </div>
        </div>
      )
    } else {
      return (
        <Tooltip
          position="right top"
          trigger={({ DefaultTriggerComponent }) => (
            <div className="flex space-x-1 text-gray-300">
              <label className={topLabelClasses}>{label}</label>
              <DefaultTriggerComponent />
            </div>
          )}
          className="w-[358px]"
        >
          Estimated value is based on Droppp Marketplace data and is for
          illustrative purposes only. There is no assurance that the value of
          your collection will match the figures presented. This estimate can be
          delayed up to 15 minutes.
        </Tooltip>
      )
    }
  }
  return (
    <div>
      {toolTip ? (
        renderToolTip()
      ) : (
        <div className={topLabelClasses}>{label}</div>
      )}
      <div className="flex items-center gap-half">
        {isUsdc ? (
          <USDCValueText
            selectionMode={selectionMode}
            value={value.value || 0}
          />
        ) : (
          <>
            <Icon name="arcade" className="w-2 h-2" />
            <PointsValueText
              selectionMode={selectionMode}
              value={value.value || 0}
            />
          </>
        )}
      </div>
    </div>
  )
}
